// Maps an Ad from the public API onto the legacy Vehicle model the ad page's
// components render (codegen/types.ts), so the page keeps its layout and
// behaviour while the data comes from ads.byAdId.

import i18next from 'i18next';
import {
  Accessory,
  Branch,
  ContactOptions,
  FinancialOption,
  FinancialOptionDetail,
  Insurance,
  InsuranceOption,
  Media,
  OpeningHours,
  PackageOption,
  Vehicle,
} from '../../@types/codegen/types';
import { Category, Property, Tree } from '../../@types/vehicle-properties';
import {
  Ad,
  AdMediaItem,
  AttributeCategory,
  AttributeValue,
  InsuranceInquiryInsurance,
  LeasingPrice,
  PaymentRange,
  PaymentResponse,
} from './types';

const num = (value: string | number | null | undefined) => {
  if (value === null || value === undefined || value === '') return undefined;
  const n = typeof value === 'number' ? value : Number.parseFloat(value);
  return Number.isFinite(n) ? n : undefined;
};

const str = (value: string | null | undefined) => (value ? value : undefined);

type DayKey = 'monday' | 'tuesday' | 'wednesday' | 'thursday' | 'friday' | 'saturday' | 'sunday';

const DAY_KEYS: Record<string, DayKey> = {
  MONDAY: 'monday',
  TUESDAY: 'tuesday',
  WEDNESDAY: 'wednesday',
  THURSDAY: 'thursday',
  FRIDAY: 'friday',
  SATURDAY: 'saturday',
  SUNDAY: 'sunday',
};

const toOpeningHours = (ad: Ad): OpeningHours | null => {
  const hours = ad.branch.weekOpeningHours?.openingHours;
  if (!hours?.length) return null;
  const result: Partial<Record<DayKey, { from: string; until: string } | null>> = {};
  hours.forEach((h) => {
    const key = h && DAY_KEYS[h.dayOfWeek];
    if (!key) return;
    result[key] = h?.openFrom && h?.openTo ? { from: h.openFrom, until: h.openTo } : null;
  });
  return result as OpeningHours;
};

const toMedia = (items: AdMediaItem[]): Media[] =>
  [...items]
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map<Media | undefined>((m) => {
      if (m.__typename === 'AdImage' && m.url) {
        return {
          type: 'image',
          files: [
            {
              url: m.url,
              formats: (m.formats || []).map((f) => ({ format: f.format, url: f.url })),
            },
          ],
        };
      }
      if (m.__typename === 'Ad360ImageGroup' && m.images?.length) {
        return { type: 'threesixty', files: m.images.map((i) => ({ url: i.url, formats: [] })) };
      }
      if (m.__typename === 'AdEmbeddedVideo' && m.externalUrl) {
        return { type: 'embedded', files: [{ url: m.externalUrl, formats: [] }] };
      }
      return undefined;
    })
    .filter((m): m is Media => !!m);

const toDetail = (
  range: PaymentRange | null | undefined,
  scale = 1
): FinancialOptionDetail | undefined =>
  range
    ? {
        current: (num(range.current) ?? 0) * scale,
        default: (num(range.default) ?? 0) * scale,
        min: (num(range.min) ?? 0) * scale,
        max: (num(range.max) ?? 0) * scale,
        step: (num(range.step) ?? 0) * scale,
      }
    : undefined;

/** A loan from paymentDetailsList or calculatePayment as the legacy FinancialOption. */
export const toLoanOption = (p: PaymentResponse): FinancialOption => ({
  type: 'loan',
  identifier: p.financialOptionId,
  name: str(p.name) || p.financialInstitution,
  description: str(p.description),
  image: str(p.imageUrl),
  logotype: str(p.imageUrl),
  url: str(p.link),
  interest: num(p.interest),
  effectiveInterest: num(p.effectiveInterest),
  setupFee: num(p.setupFee),
  administrationFee: num(p.administrationFee),
  loanAmount: num(p.loanAmount),
  monthlyCost: num(p.monthlyCost),
  totalCreditCost: num(p.totalCreditCost),
  totalResidualValue: num(p.totalResidualValue),
  duration: toDetail(p.duration),
  downPayment: toDetail(p.downPayment),
  residual: toDetail(p.residual),
  mileage: null,
  includes: [],
});

const toLeasingOptions = (ad: Ad): FinancialOption[] => {
  const options: FinancialOption[] = [];
  const pairs: [LeasingPrice | null, string, boolean][] = [
    [ad.pricing?.leasing ?? null, 'leasing', false],
    [ad.pricing?.companyLeasing ?? null, 'business-leasing', true],
  ];
  pairs.forEach(([price, type, business]) => {
    const monthlyCost = num(price?.leasingMonthlyPayment?.amount);
    if (!price || !monthlyCost) return;
    const promo = ad.leasingPromos.find((lp) => lp.isBusinessLeasing === business);
    const months = promo?.periodMonths || price.contractMonthsRemaining || undefined;
    const mileage = promo?.mileagePerYear || undefined;
    options.push({
      type,
      name: str(promo?.title) || price.issuer || undefined,
      description: str(promo?.description),
      image: str(promo?.image),
      monthlyCost,
      duration: months
        ? { current: months, default: months, min: months, max: months, step: 0 }
        : undefined,
      mileage: mileage
        ? { current: mileage, default: mileage, min: mileage, max: mileage, step: 0 }
        : undefined,
      link: promo?.link ? { url: promo.link.url, title: promo.link.name } : undefined,
      includes: [],
    });
  });
  return options;
};

const toInsuranceOptions = (ad: Ad): InsuranceOption[] => {
  const p = ad.iteration?.commerce.insuranceProvider;
  if (!p) return [];
  return [
    {
      name: p.title,
      institute: p.institute,
      identifier: p.identifier,
      logotype: str(p.logo),
      description: str(p.description),
      longDescription: str(p.longDescription),
      url: str(p.url),
      insuranceHeader: str(p.insuranceHeader),
      insuranceText: str(p.ecomInsuranceText),
      requiresDistance: p.requiresDistance,
      requiresPersonalNumber: p.requiresPersonalNumber,
    },
  ];
};

const toAccessories = (ad: Ad): Accessory[] =>
  ad.accessories.map((a) => ({
    id: a.id,
    name: a.name,
    price: a.price ?? 0,
    salePrice: a.salePrice,
    assemblyPrice: a.assemblyPrice,
    articleNumber: str(a.articleNumber),
    description: a.longDescription || '',
    excerpt: a.shortDescription || '',
    images: [...a.media].sort((x, y) => x.sortOrder - y.sortOrder).map((m) => m.url),
    logotype: str(a.logoUrl),
    manufacturer: str(a.manufacturer),
    model: str(a.model),
    productPage: a.productPageLink
      ? { url: a.productPageLink, title: str(a.productPageLinkText) }
      : undefined,
  }));

const toPackageOptions = (ad: Ad): PackageOption[] =>
  ad.packaging.selectedPackaging.map((p) => ({
    title: p.title,
    description: str(p.description),
    image: str(p.imageUrl),
    link: p.ctaUrl ? { url: p.ctaUrl, title: str(p.ctaName) } : undefined,
  }));

/** The manufacturer's warranty package, shown as "Inkl. … begagnatgaranti". */
const toManufacturerPackageOption = (ad: Ad): PackageOption | undefined => {
  if (!ad.packaging.certifiedPreOwned) return undefined;
  const warranty = ad.offerings.warranties.find((w) => w.included) || ad.offerings.warranties[0];
  if (!warranty) return undefined;
  const { definition } = warranty;
  return {
    title: definition.name,
    description: str(definition.description) || str(warranty.text),
    image: str(definition.logoUrl),
    link: definition.externalUrl ? { url: definition.externalUrl } : undefined,
  };
};

const toContact = (ad: Ad): ContactOptions | undefined => {
  const c = ad.contactOption;
  if (c.__typename === 'AssignedUser' || c.__typename === 'AdDefaultContactOptions') {
    return { name: str(c.displayName), email: str(c.email), phonenumber: str(c.telephone) };
  }
  const d = ad.branch.adDefaultContactOptions;
  if (d && (d.email || d.telephone)) {
    return {
      name: str(d.displayName),
      email: str(d.email),
      phonenumber: str(d.telephone),
      avatar: str(d.avatarUrl),
    };
  }
  return undefined;
};

const toBranch = (ad: Ad): Branch => {
  const b = ad.branch;
  return {
    id: b.id,
    name: str(b.displayName),
    description: str(b.description),
    homepage: str(b.homePage),
    logotype: str(b.logo),
    location: {
      city: str(b.city),
      streetAddress: str(b.street),
      postalCode: str(b.zip),
      position:
        b.latitude !== null && b.longitude !== null
          ? { latitude: b.latitude, longitude: b.longitude }
          : undefined,
    },
    contact: {
      email: str(b.contactInformation?.email),
      phonenumber: str(b.contactInformation?.telephone),
    },
    openingHours: toOpeningHours(ad),
    flags: {
      mrf: !!b.isAutomotiveAssociationVerified,
      ecommerce: !!ad.iteration?.commerce.enabled,
      authorizedReseller: ad.authorizedResellerForManufacturer,
      centralStorage: false,
    },
    connections: [],
    promos: b.promos.map((p) => ({
      title: str(p.title),
      description: str(p.description),
      image: str(p.image),
      link: p.link ? { url: p.link.url, title: p.link.name } : undefined,
    })),
    services: [],
  };
};

const platformChannel = (ad: Ad) => ad.channels.find((c) => c.__typename === 'PlatformChannel');

const toProperty = (v: AttributeValue): Property => {
  const isBoolean =
    v.__typename === 'BooleanProperty' ||
    v.formattedValue === 'true' ||
    v.formattedValue === 'false';
  return {
    name: v.label,
    type: isBoolean ? 'boolean' : 'text',
    value: isBoolean ? v.formattedValue === 'true' : v.formattedValue,
    unit: null,
    hint: null,
  };
};

const toCategory = (c: AttributeCategory, id: number): Category => ({
  id,
  name: c.displayName,
  properties: c.values.map(toProperty),
  subCategories: (c.categories || [])
    .filter((s) => s.values.length)
    .map((s, i) => toCategory(s, id * 100 + i + 1)),
});

/** The "Fordonsdata" tabs: the vehicle's attributes by category, basics first. */
export const toPropertyTree = (ad: Ad): Tree => {
  const item = ad.iteration?.item;
  const odometer = ad.iteration?.logistics?.odometerReading;
  const basic: Property[] = [];
  const add = (name: string, value?: string | number | null, unit?: string, hint?: string) => {
    if (value !== undefined && value !== null && value !== '') {
      basic.push({
        name,
        value,
        type: typeof value === 'number' ? 'number' : 'text',
        unit: unit || null,
        hint: hint || null,
      });
    }
  };
  add(i18next.t('customProperty.manufacturer.displayName'), item?.manufacturer?.stringValue);
  add(i18next.t('customProperty.modelSeries.displayName'), item?.modelSeries?.stringValue);
  add(
    i18next.t('customProperty.modelName.displayName'),
    item?.modelName?.stringValue,
    undefined,
    i18next.t('customProperty.modelName.hint')
  );
  add(
    i18next.t('customProperty.modelYear.displayName'),
    item?.modelYear?.intValue,
    undefined,
    i18next.t('customProperty.modelYear.hint')
  );
  add(
    i18next.t('customProperty.manufactureYear.displayName'),
    item?.manufactureYear?.intValue,
    undefined,
    i18next.t('customProperty.manufactureYear.hint')
  );
  if (odometer) {
    add(
      i18next.t('customProperty.odometer.displayName'),
      odometer.value,
      i18next.t(`odometer.${odometer.unit === 'KILOMETER' ? 'Kilometer' : 'ScandinavianMile'}`),
      i18next.t('customProperty.odometer.hint')
    );
  }
  add(
    i18next.t('customProperty.gearboxType.displayName'),
    item?.attributes?.transmission?.formattedValue
  );
  add(
    i18next.t('customProperty.registrationNumber.displayName'),
    item?.registrationNumber?.stringValue
  );
  add(
    i18next.t('customProperty.fuelType.displayName'),
    item?.driveline?.fuelTypesString?.stringValue,
    undefined,
    i18next.t('customProperty.fuelType.hint')
  );
  add(
    i18next.t('customProperty.enginePower.displayName'),
    item?.attributes?.enginePower?.formattedValue
  );

  const categories = (item?.attributes?.byCategory || [])
    .map((c, i) => toCategory(c, i + 1))
    .filter((c) => c.properties.length || c.subCategories.length);

  const basicIndex = categories.findIndex((c) => c.name === i18next.t('category.basic'));
  if (basicIndex > -1) {
    const [existing] = categories.splice(basicIndex, 1);
    categories.unshift({ ...existing, properties: [...basic, ...existing.properties] });
  } else {
    categories.unshift({
      id: 1000,
      name: i18next.t('category.basic'),
      properties: basic,
      subCategories: [],
    });
  }
  return { categories };
};

export const toInsurances = (insurances: (InsuranceInquiryInsurance | null)[]): Insurance[] =>
  insurances
    .filter((i): i is InsuranceInquiryInsurance => !!i)
    .map((i) => ({
      name: i.name,
      price: i.price,
      unit: i.unit,
      description: i.description,
      logotype: str(i.details?.logo) || str(i.branding),
      url: str(i.details?.terms),
      terms: str(i.details?.terms),
      legality: { description: str(i.legalDescription), url: str(i.legalUrl) },
      items: i.insuranceItems.map((x) => ({ name: x.name, description: x.description })),
      addons: (i.addons || []).map((a) => ({
        id: a.id,
        title: a.title,
        description: a.description,
        price: a.price,
        unit: i.unit,
        exclusions: a.exclude || [],
      })),
    }));

export const toVehicle = (ad: Ad): Vehicle => {
  const item = ad.iteration?.item;
  const odometer = ad.iteration?.logistics?.odometerReading;
  const channel = platformChannel(ad);
  const price = num(ad.pricing?.cash?.price?.amount) ?? 0;
  const oldPrice = num(ad.pricing?.cash?.oldPrice?.amount);
  const commerce = ad.iteration?.commerce;
  const manufacturerPackage = toManufacturerPackageOption(ad);
  const deliveryDate =
    channel?.inventoryStatus !== 'InStock' && channel?.deliveryTime?.__typename === 'DeliveryDate'
      ? channel.deliveryTime.date
      : undefined;

  return {
    id: ad.id,
    title: item?.displayName || ad.iteration?.displayName || ad.salesDescription?.title || '',
    shortDescription: str(ad.salesDescription?.title),
    description: str(ad.salesDescription?.enriched),
    price,
    discount: oldPrice && oldPrice > price ? oldPrice - price : undefined,
    deductibleVat: !!ad.pricing?.cash?.deductibleVat,
    publishedAt: channel?.publishedAt ? new Date(channel.publishedAt) : undefined,
    availableFrom: deliveryDate ? new Date(deliveryDate) : undefined,
    flags: { demoVersion: ad.packaging.isDemoVersion, auction: false, testDrive: false },
    media: toMedia(ad.media.items),
    documents: (ad.iteration?.documents.public || []).map((d) => ({
      webp: '',
      category: d.category || 'other',
      contentType: d.contentType,
      name: d.name,
      url: d.uri,
    })),
    accessories: toAccessories(ad),
    packageOptions: toPackageOptions(ad),
    manufacturer: {
      name: item?.manufacturer?.stringValue || '',
      logotype: manufacturerPackage?.image,
      packageOption: manufacturerPackage,
    },
    financialOptions: [
      ...(commerce?.paymentDetailsList || []).map(toLoanOption),
      ...toLeasingOptions(ad),
    ],
    insuranceOptions: toInsuranceOptions(ad),
    ecommerce: commerce
      ? {
          enabled: commerce.enabled && !commerce.disabledOnWayke,
          reserved: commerce.reserved,
          withTradeIn: commerce.allowTradeIn,
          withHomeDelivery: commerce.homeDelivery,
        }
      : undefined,
    contact: toContact(ad),
    branch: toBranch(ad),
    location: toBranch(ad).location,
    safety: [],
    data: {
      manufacturer: item?.manufacturer?.stringValue || '',
      modelSeries: str(item?.modelSeries?.stringValue),
      modelName: str(item?.modelName?.stringValue),
      modelYear: item?.modelYear?.intValue || 0,
      manufactureYear: item?.manufactureYear?.intValue || 0,
      registrationNumber: str(item?.registrationNumber?.stringValue),
      vin: str(item?.vin?.formattedValue),
      salesName: str(item?.salesName?.stringValue),
      fuelType: str(item?.driveline?.fuelTypesString?.stringValue),
      gearboxType: str(item?.attributes?.transmission?.formattedValue),
      gearbox: str(item?.attributes?.transmission?.formattedValue),
      enginePower: item?.attributes?.enginePower?.intValue ?? undefined,
      mileage: odometer?.value || 0,
      odometerReading: odometer
        ? {
            value: odometer.value,
            unit: odometer.unit === 'KILOMETER' ? 'Kilometer' : 'ScandinavianMile',
          }
        : undefined,
      options: (ad.equipment?.items || []).map((e) => e.name),
      properties: {},
      propertySet: undefined,
    },
  };
};
